import {composeReducers} from 'common/util/composeReducers';
import lodash from 'lodash';
import {
    BINDER_TEMPLATE_QUERY_WRITE_QUERY,
    BINDER_TEMPLATE_QUERY_WRITE_NODE,
    BINDER_TEMPLATE_QUERY_WRITE_VALUESET,
    BINDER_TEMPLATE_QUERY_ADD_CHILD_NODE,
    BINDER_TEMPLATE_QUERY_REMOVE_CHILD_NODE,
    BINDER_TEMPLATE_QUERY_REMOVE_ALL_CHILD_NODES,
    BINDER_TEMPLATE_QUERY_WRAP_IN_NEW_PARENT,
    BINDER_TEMPLATE_QUERY_WRITE_QUERIES,
    BINDER_TEMPLATE_QUERY_WRITE_SELECTED_QUERY_ID,
    BINDER_TEMPLATE_QUERY_WRITE_REMOVE_DOCUMENT_TYPE_FROM_QUERY,
    BINDER_TEMPLATE_QUERY_WRITE_REMOVE_QUERY,
    BINDER_TEMPLATE_QUERY_CHANGE_DISPLAY_ORDER,
    BINDER_TEMPLATE_QUERY_WRITE_OPTION_VALUE,
    BINDER_TEMPLATE_QUERY_MANIPULATE_OPTION,
    BINDER_TEMPLATE_REMOVE_QUERIES
} from 'complionAdmin/actions/binderTemplateQuery';

export function writeQuery(state, action) {
    if (!action || action.type !== BINDER_TEMPLATE_QUERY_WRITE_QUERY) {
        return state;
    }

    const {id, data} = action.payload;
    const queries = lodash.get(state, 'queries', {});
    const updatedQueries = {...queries};
    updatedQueries[id] = lodash.cloneDeep(data);
    let parentQuery = lodash.get(queries, data.parentId);
    if (parentQuery) {
        parentQuery.childIds = lodash.union(parentQuery.childIds, [data.queryId]);
        updatedQueries[parentQuery.queryId] = {...parentQuery};
    }
    return {...state, queries: updatedQueries};
}

export function writeNode(state, action) {
    if (!action || action.type !== BINDER_TEMPLATE_QUERY_WRITE_NODE) {
        return state;
    }

    const {id, data} = action.payload;
    const nodes = lodash.get(state, 'nodes', {});

    const updatedNodes = {...nodes};
    updatedNodes[id] = lodash.cloneDeep(data);
    return {...state, nodes: updatedNodes};
}

export function writeValueSets(state, action) {
    if (!action || action.type !== BINDER_TEMPLATE_QUERY_WRITE_VALUESET) {
        return state;
    }

    const {id, data} = action.payload;
    const valueSets = lodash.get(state, 'valueSets', {});

    if (lodash.isEqual(valueSets[id], data)) {
        return state;
    }

    const updatedNodes = {...valueSets};
    updatedNodes[id] = lodash.cloneDeep(data);
    return {...state, valueSets: updatedNodes};
}

export function addChildNode(state, action) {
    if (!action || action.type !== BINDER_TEMPLATE_QUERY_ADD_CHILD_NODE) {
        return state;
    }

    const {parentId, nodeId, data, placementNodeId, placementMethod} = action.payload;
    const nodes = lodash.get(state, 'nodes', {});
    const parentNode = lodash.get(nodes, parentId);
    if (!parentNode) {
        throw new Error('Could not find a parent node to update');
    }
    const updatedParentNode = {...parentNode};
    let placementIndex, newChildIds;
    switch (placementMethod) {
        case 'before':
            placementIndex = parentNode.childIds.indexOf(placementNodeId);
            newChildIds = [
                ...parentNode.childIds.slice(0, placementIndex),
                nodeId,
                ...parentNode.childIds.slice(placementIndex)
            ];
            break;
        case 'after':
            placementIndex = parentNode.childIds.indexOf(placementNodeId);
            newChildIds = [
                ...parentNode.childIds.slice(0, placementIndex + 1),
                nodeId,
                ...parentNode.childIds.slice(placementIndex + 1)
            ];
            break;
        default:
            // case: 'append'
            newChildIds = [...parentNode.childIds, nodeId];
            break;
    }
    updatedParentNode.childIds = newChildIds;

    const updatedNodes = {...nodes};
    updatedNodes[parentId] = updatedParentNode;
    updatedNodes[nodeId] = lodash.cloneDeep(data);

    return {
        ...state,
        nodes: updatedNodes
    };
}


export function removeNodeFromParent(state, action) {
    if (!action || action.type !== BINDER_TEMPLATE_QUERY_REMOVE_CHILD_NODE) {
        return state;
    }

    const {parentId, childId} = action.payload;
    const nodes = lodash.get(state, 'nodes', {});
    const parentNode = lodash.get(nodes, parentId);
    if (!parentNode) {
        throw new Error('Could not find a parent node to update');
    }

    const childIdIndex = parentNode.childIds.indexOf(childId);
    if (childIdIndex === -1) {
        return state;
    }

    const updatedParentNode = {...parentNode};
    updatedParentNode.childIds = [
        ...parentNode.childIds.slice(0, childIdIndex),
        ...parentNode.childIds.slice(childIdIndex + 1),
    ];

    const updatedNodes = {...nodes};
    updatedNodes[parentId] = updatedParentNode;

    return {
        ...state,
        nodes: updatedNodes
    };
}

export function removeAllChildNodes(state, action) {
    if (!action || action.type !== BINDER_TEMPLATE_QUERY_REMOVE_ALL_CHILD_NODES) {
        return state;
    }

    const {parentId} = action.payload;
    const nodes = lodash.get(state, 'nodes', {});
    const parentNode = lodash.get(nodes, parentId);
    if (!parentNode) {
        throw new Error('Could not find a parent node to update');
    }
    const updatedParentNode = {...parentNode};
    updatedParentNode.childIds = [];
    const updatedNodes = {...nodes};
    updatedNodes[parentId] = updatedParentNode;
    return {
        ...state,
        nodes: updatedNodes
    };

}

export function wrapInNewParent(state, action) {
    if (!action || action.type !== BINDER_TEMPLATE_QUERY_WRAP_IN_NEW_PARENT) {
        return state;
    }

    const {parentId, childId, nodeId, data} = action.payload;
    const nodes = lodash.get(state, 'nodes', {});
    const parentNode = lodash.get(nodes, parentId);
    if (!parentNode) {
        throw new Error('Could not find a parent node to update');
    }
    const updatedParentNode = {...parentNode};
    const childIdIndex = parentNode.childIds.indexOf(childId);
    updatedParentNode.childIds = [
        ...parentNode.childIds.slice(0, childIdIndex),
        nodeId,
        ...parentNode.childIds.slice(childIdIndex + 1)
    ];
    const childNode = lodash.get(nodes, childId);
    const updatedChildNode = {...childNode};
    updatedChildNode.parentId = nodeId;

    const updatedNodes = {...nodes};
    updatedNodes[parentId] = updatedParentNode;
    updatedNodes[nodeId] = lodash.cloneDeep(data);
    updatedNodes[childId] = updatedChildNode;

    return {
        ...state,
        nodes: updatedNodes
    };
}

export function writeBinderTemplateQueries(state, action) {
    if (!action || action.type !== BINDER_TEMPLATE_QUERY_WRITE_QUERIES) {
        return state;
    }

    const {binderTemplateId, queries} = action.payload;
    const binderTemplates = {...state.binderTemplates};
    const binderTemplate = {...binderTemplates[binderTemplateId]};
    binderTemplates[binderTemplateId] = binderTemplate;
    binderTemplate.queries = lodash.cloneDeep(queries);
    return {
        ...state,
        binderTemplates
    };
}

export function writeSelectedQueryId(state, action) {
    if (!action || action.type !== BINDER_TEMPLATE_QUERY_WRITE_SELECTED_QUERY_ID) {
        return state;
    }

    const selectedQueryId = action.payload;
    const app = {...lodash.get(state, 'app')};
    app.selectedQueryId = selectedQueryId;

    return {
        ...state,
        app
    };
}

export function writeRemoveDocumentTypeFromQuery(state, action) {
    if (!action || action.type !== BINDER_TEMPLATE_QUERY_WRITE_REMOVE_DOCUMENT_TYPE_FROM_QUERY) {
        return state;
    }

    const {queryId, documentTypeId} = action.payload;
    const newState = lodash.clone(state);

    function removeDocumentTypeIdFromQuery(transientState, id) {
        const query = lodash.get(transientState, ['queries', id]);
        const nodes = lodash.get(transientState, 'nodes');
        const rootNode = lodash.cloneDeep(lodash.get(nodes, query.rootNodeId));
        const documentTypeNodeIds = [];
        const newNodes = {};
        lodash.forEach(nodes, node => {
            const isDocumentTypeNode =
                node.parentId === rootNode.nodeId &&
                node.type === 'DocumentTypePredicate' &&
                node.data.documentType.id === documentTypeId;

            if (isDocumentTypeNode) {
                documentTypeNodeIds.push(node.nodeId);
            } else {
                newNodes[node.nodeId] = node;
            }
        });
        rootNode.childIds = lodash.difference(rootNode.childIds, documentTypeNodeIds);
        lodash.set(transientState, 'nodes', newNodes);
        lodash.set(transientState, ['nodes', rootNode.nodeId], rootNode);
        lodash.set(transientState, 'queries', {...lodash.get(transientState, 'queries')});
        lodash.set(transientState, ['queries', id], lodash.cloneDeep(query));
        return lodash.reduce(query.childIds, (nextTransientState, childId) => {
            return removeDocumentTypeIdFromQuery(nextTransientState, childId);
        }, transientState);
    }

    return removeDocumentTypeIdFromQuery(newState, queryId);
}

export function writeRemoveChildQuery(state, action) {
    if (!action || action.type !== BINDER_TEMPLATE_QUERY_WRITE_REMOVE_QUERY) {
        return state;
    }

    const targetQueryId = action.payload;
    const currentQueries = state.queries;
    const targetQuery = currentQueries[targetQueryId];

    if (!targetQuery || lodash.isNil(targetQuery.parentId)) {
        // cannot remove root query node
        return state;
    }

    const queriesToRemove = [];
    const queryQueue = [targetQuery.queryId];
    const maxIterations = 5000;
    let iterations = 0;

    while (queryQueue.length > 0) {
        iterations += 1;
        if (iterations > maxIterations) {
            throw new Error('Could not remove query. Maximum iterations exceeded.');
        }
        const nextQueryId = queryQueue.shift();
        const nextQuery = currentQueries[nextQueryId];
        queriesToRemove.push(nextQuery.queryId);
        queryQueue.push(...nextQuery.childIds);
    }

    const newQueries = lodash.chain(currentQueries)
        .filter(q => !queriesToRemove.includes(q.queryId))
        .keyBy('queryId')
        .value();
    const targetQueryParent = currentQueries[targetQuery.parentId];
    const newParentQuery = {...targetQueryParent};
    newParentQuery.childIds = lodash.filter(newParentQuery.childIds, id => id !== targetQueryId);
    newQueries[targetQuery.parentId] = newParentQuery;

    return {
        ...state,
        queries: newQueries
    };
}

export function changeDisplayOrder(state, action) {
    if (!action || action.type !== BINDER_TEMPLATE_QUERY_CHANGE_DISPLAY_ORDER) {
        return state;
    }

    const {queryId, relative} = action.payload;
    const queries = state.queries;
    const query = queries[queryId];
    if (query.parentId === null) {
        //only one root element, no need to change it.
        return state;
    }
    const parentQuery = queries[query.parentId];
    const children = lodash.map(parentQuery.childIds, childId => queries[childId]);
    const sortedChildren = lodash.sortBy(children, child => lodash.get(child, 'data.displayOrder', 0));
    const childIdIndex = lodash.findIndex(sortedChildren, child => child.queryId === queryId);

    let newChildIndex;
    switch (relative) {
        case 'up':
            newChildIndex = Math.max(childIdIndex - 1, 0);
            break;
        case 'down':
            newChildIndex = Math.min(childIdIndex + 1, sortedChildren.length - 1);
            break;
        default:
            //no change needed, just return state
            return state;
    }

    var swappedChild = sortedChildren[childIdIndex];
    sortedChildren[childIdIndex] = sortedChildren[newChildIndex];
    sortedChildren[newChildIndex] = swappedChild;

    const newQueries = {...queries};
    for (let childIndex = 0; childIndex < sortedChildren.length; childIndex += 1) {
        sortedChildren[childIndex] = {...sortedChildren[childIndex]};
        sortedChildren[childIndex].data = {...sortedChildren[childIndex].data, displayOrder: (childIndex + 1) * 1000};
        newQueries[sortedChildren[childIndex].queryId] = sortedChildren[childIndex];
    }
    const newParentQuery = {...parentQuery};
    newParentQuery.childIds = sortedChildren.map(c => c.queryId);
    newQueries[newParentQuery.queryId] = newParentQuery;


    return {
        ...state,
        queries: newQueries
    };
}

export function writeQueryOptionValue(state, action) {
    if (!action || action.type !== BINDER_TEMPLATE_QUERY_WRITE_OPTION_VALUE) {
        return state;
    }

    const {
        queryId,
        optionsKey,
        propertyName,
        keyIndex,
        value
    } = action.payload;

    const queries = {...state.queries};
    const query = {...queries[queryId]};
    const data = {...query.data};
    const options = {...data.options};
    const optionsSection = [...options[optionsKey]];
    const optionsSectionItem = {...optionsSection[keyIndex]};

    optionsSectionItem[propertyName] = value;
    optionsSection[keyIndex] = optionsSectionItem;
    options[optionsKey] = optionsSection;
    data.options = options;
    query.data = data;
    queries[queryId] = query;

    return {
        ...state,
        queries
    };
}

export function writeQueryOptionManipulation(state, action) {
    if (!action || action.type !== BINDER_TEMPLATE_QUERY_MANIPULATE_OPTION) {
        return state;
    }

    const {
        queryId,
        optionsKey,
        keyIndex,
        actionName
    } = action.payload;

    if (!['moveUp', 'moveDown', 'add', 'remove'].includes(actionName)) {
        return state;
    }

    const queries = {...state.queries};
    const query = {...queries[queryId]};
    const data = {...query.data};
    const options = {...data.options};
    let optionsSection = [...options[optionsKey]];
    let newIndex, oldValue, newValue;

    switch (actionName) {
        case 'moveUp':
            newIndex = Math.max(keyIndex - 1, 0);
            oldValue = optionsSection[newIndex];
            newValue = optionsSection[keyIndex];
            optionsSection[keyIndex] = oldValue;
            optionsSection[newIndex] = newValue;
            break;
        case 'moveDown':
            newIndex = Math.min(keyIndex + 1, optionsSection.length - 1);
            oldValue = optionsSection[newIndex];
            newValue = optionsSection[keyIndex];
            optionsSection[keyIndex] = oldValue;
            optionsSection[newIndex] = newValue;
            break;
        case 'add':
            // note: options must be able to be created as empty objects
            newValue = {};
            optionsSection = [
                ...optionsSection.slice(0, keyIndex + 1),
                newValue,
                ...optionsSection.slice(keyIndex + 1)
            ];
            break;
        case 'remove':
            optionsSection = [
                ...optionsSection.slice(0, keyIndex),
                ...optionsSection.slice(keyIndex + 1),
            ];
            break;
        default:
            return state;
    }

    options[optionsKey] = optionsSection;
    data.options = options;
    query.data = data;
    queries[queryId] = query;

    return {
        ...state,
        queries
    };
}

function removeQueries(state, action) {
    if (action.type !== BINDER_TEMPLATE_REMOVE_QUERIES) {
        return state;
    }

    const newState = { ...state };
    lodash.unset(newState, 'queries');
    return newState;
}

export default composeReducers(
    writeQuery,
    writeNode,
    writeValueSets,
    addChildNode,
    removeNodeFromParent,
    removeAllChildNodes,
    wrapInNewParent,
    writeBinderTemplateQueries,
    writeSelectedQueryId,
    writeRemoveDocumentTypeFromQuery,
    writeRemoveChildQuery,
    changeDisplayOrder,
    writeQueryOptionValue,
    writeQueryOptionManipulation,
    removeQueries
);
